/**
 * Season-wide "average opponent rating faced" baseline for the Session Summary tab.
 *
 * Powers the "+X vs your season average" comparison on the Summary tab's
 * "Average opponent rating" card. Deliberately separate from
 * sessionPlayerInsights.js (which is a pure, no-query transform) since this
 * needs new data outside the current session — see
 * docs/sessions/SESSION_LIFECYCLE_ARCHITECTURE.md §3.7 for the tradeoffs.
 *
 * Two sequential round trips:
 *   1) My own elo_history rows this season at this club, excluding the
 *      current session — gives the list of historical session_game_ids
 *      (+ pair_type + my own old_value).
 *   2) For those game ids: players_session_games (to know team_number per
 *      participant) and elo_history (old_value for every participant),
 *      fetched in parallel. Combined client-side to get each historical
 *      game's opponent-team average old_value.
 */
import { supabase } from '../utils/supabase';

/**
 * @param {object} params
 * @param {string} params.playerId
 * @param {string} params.clubId
 * @param {string} params.seasonId
 * @param {string} params.excludeSessionId - the current session, excluded from the baseline
 * @returns {Promise<Array<{ sessionGameId: string, pairType: string|null, oppAvgElo: number }>>}
 */
export async function fetchSeasonOpponentRatingBaseline({ playerId, clubId, seasonId, excludeSessionId }) {
  if (!playerId || !clubId || !seasonId) return [];

  let myHistoryQuery = supabase
    .from('elo_history')
    .select('session_game_id, pair_type')
    .eq('player_id', playerId)
    .eq('club_id', clubId)
    .eq('season_id', seasonId)
    .not('session_game_id', 'is', null);

  if (excludeSessionId) {
    myHistoryQuery = myHistoryQuery.neq('session_id', excludeSessionId);
  }

  const { data: myHistory, error: myHistoryError } = await myHistoryQuery;

  if (myHistoryError) throw myHistoryError;
  if (!myHistory?.length) return [];

  const gameEntries = myHistory.reduce((map, row) => {
    if (!map.has(row.session_game_id)) map.set(row.session_game_id, row.pair_type);
    return map;
  }, new Map());

  const gameIds = Array.from(gameEntries.keys());
  if (!gameIds.length) return [];

  const [participantsResult, allEloResult] = await Promise.all([
    supabase
      .from('players_session_games')
      .select('session_game_id, player_id, team_number')
      .in('session_game_id', gameIds),
    supabase
      .from('elo_history')
      .select('session_game_id, player_id, old_value')
      .in('session_game_id', gameIds)
      .eq('club_id', clubId),
  ]);

  if (participantsResult.error) throw participantsResult.error;
  if (allEloResult.error) throw allEloResult.error;

  const participantsByGame = new Map();
  (participantsResult.data || []).forEach((row) => {
    if (!participantsByGame.has(row.session_game_id)) participantsByGame.set(row.session_game_id, []);
    participantsByGame.get(row.session_game_id).push(row);
  });

  const eloByGamePlayer = new Map();
  (allEloResult.data || []).forEach((row) => {
    eloByGamePlayer.set(`${row.session_game_id}:${row.player_id}`, row.old_value);
  });

  const baseline = [];
  gameIds.forEach((gameId) => {
    const participants = participantsByGame.get(gameId) || [];
    const myRow = participants.find((p) => p.player_id === playerId);
    if (!myRow) return;

    const opponentIds = participants
      .filter((p) => p.team_number !== myRow.team_number)
      .map((p) => p.player_id);
    if (!opponentIds.length) return;

    const opponentRatings = opponentIds
      .map((id) => eloByGamePlayer.get(`${gameId}:${id}`))
      .filter((v) => v != null);
    if (!opponentRatings.length) return;

    const oppAvgElo = opponentRatings.reduce((sum, v) => sum + v, 0) / opponentRatings.length;
    baseline.push({ sessionGameId: gameId, pairType: gameEntries.get(gameId) ?? null, oppAvgElo });
  });

  return baseline;
}

/**
 * Reduces the raw baseline rows to a single average, scoped to a ladder view.
 * Pure — kept here (rather than sessionPlayerInsights.js) since it operates on
 * this service's own return shape.
 *
 * @param {Array} baseline - from fetchSeasonOpponentRatingBaseline
 * @param {(pairType: string|null, ladderView: string) => boolean} isGameInLadder
 * @param {'all'|'mixed'|'levels'} ladderView
 * @returns {{ average: number|null, games: number }}
 */
export function summarizeOpponentRatingBaseline(baseline, isGameInLadder, ladderView) {
  const scoped = (baseline || []).filter((row) => isGameInLadder(row.pairType, ladderView));
  if (!scoped.length) return { average: null, games: 0 };
  const average = scoped.reduce((sum, row) => sum + row.oppAvgElo, 0) / scoped.length;
  return { average, games: scoped.length };
}

/**
 * Current club+season ratings for a set of opponents, for the "Opponents this
 * session" list. Ratings live in `player_club_elo_ratings` (club+season
 * scoped), not on `players` directly — see e.g. scheduledGamesService.js for
 * the same pattern.
 *
 * @param {Array<string>} opponentIds
 * @param {string} clubId
 * @param {string} seasonId
 * @returns {Promise<Object>} map of opponentId -> { mixed, levels }
 */
export async function fetchOpponentCurrentRatings(opponentIds, clubId, seasonId) {
  const uniqueIds = Array.from(new Set((opponentIds || []).filter(Boolean)));
  if (!uniqueIds.length || !clubId || !seasonId) return {};

  const { data, error } = await supabase
    .from('player_club_elo_ratings')
    .select('player_id, mixed_elo_rating, levels_elo_rating')
    .eq('club_id', clubId)
    .eq('season_id', seasonId)
    .in('player_id', uniqueIds);

  if (error) throw error;

  return (data || []).reduce((map, row) => {
    map[row.player_id] = { mixed: row.mixed_elo_rating ?? null, levels: row.levels_elo_rating ?? null };
    return map;
  }, {});
}
